import './RaceRow.css';
import type { JSX } from 'preact';
import { useEffect } from 'preact/hooks';
import { AnimatedNumber } from '../../components/AnimatedNumber';
import { MiniProgressBar } from '../../components/MiniProgressBar';
import { Portrait } from '../../components/Portrait';
import { UfTile } from '../../components/UfTile';
import { WashHighlight } from '../../components/WashHighlight';
import { t } from '../../copy/strings';
import { fmtPctAdaptive, fmtSections } from '../../format/numbers';
import { placeName } from '../../format/places';
import type { RaceModel } from '../../model/governors';
import type { UfCode } from '../../model/types';
import { clearMapLink, navigate, setMapLink } from '../../state/actions';
import { partyClass, partyOf } from '../../theme/parties';
import { raceParty, tileFill } from './UfStrip';

/** Origem do destaque no mapa pedido por uma linha (uma por UF: a linha que sai da página só apaga o que acendeu). */
export const raceRowLinkSource = (uf: UfCode): string => `governors-row:${uf}`;

/** Retrato da linha com votos (§11.1 `RaceRow` item 2) e da fileira sem votos. */
const PORTRAIT_PX = 28;
const WAITING_PORTRAIT_PX = 24;

/** Situação abaixo do selo (§11.1 item 2): "eleito"/"eleita" com resultado do TSE, "definida" só com a matemática. */
export function rowStatus(r: RaceModel): string | null {
  if (r.phase !== 'decided' || !r.decision) return null;
  if (r.decision.kind === 'tse') {
    const w = r.side === null ? null : r.candidates[r.side];
    return t('gov.row.elected', { X: w?.display_name ?? '', 'X.gender': w?.gender ?? null });
  }
  return t('governors.row.decided');
}

/**
 * Nome acessível da linha (§11.1 item 7, §15.4.2): "{UF}: {A} {pctA}, {B} {pctB}, {S} das seções" apurando; com ", eleito"
 * ("eleita") pelo TSE ou ", eleição definida" pela matemática; "{UF}: aguardando as primeiras seções, 2 candidaturas"
 * sem votos. Sempre A (número menor) antes de B.
 */
export function rowAria(r: RaceModel): string {
  const UF = placeName(r.uf);
  if (r.phase === 'no_data') return t('gov.row.aria.waiting', { UF });
  const [a, b] = r.candidates;
  const vars = {
    UF,
    A: a.display_name,
    pA: fmtPctAdaptive(r.shares[0], r.shares[0], r.shares[1]),
    B: b.display_name,
    pB: fmtPctAdaptive(r.shares[1], r.shares[0], r.shares[1]),
    S: fmtSections(r.sectionsFraction),
  };
  if (r.phase === 'decided') {
    if (r.decision?.kind === 'tse') {
      const w = r.side === null ? null : r.candidates[r.side];
      return t('gov.row.aria.elected', { ...vars, X: w?.display_name ?? '', 'X.gender': w?.gender ?? null });
    }
    return t('gov.row.aria.decided', vars);
  }
  return t('gov.row.aria', vars);
}

export interface RaceRowProps {
  race: RaceModel;
  /** A UF desta linha está aberta (`aria-current="true"`). */
  current: boolean;
  /** Replay: sem lavagem, números saltam (§11.1 item 4). */
  replay: boolean;
  /** `features.photos` (M26): sem fotos, iniciais no retrato. */
  photos: boolean;
}

/**
 * Linha de disputa (§11.1 `RaceRow`): `<button>` de 78 px com o selo da UF na cor da fase e, abaixo, a situação ou a
 * minibarra de seções; à direita, os dois candidatos SEMPRE em ordem de número (retrato redondo 28, nome, percentual na
 * cor do partido com `AnimatedNumber`), nunca por votos (AC-11.1-10). Definida: a linha de quem não venceu fica apagada.
 * Troca de líder ao vivo: `WashHighlight` na cor do novo líder (nunca no replay nem com movimento reduzido). Hover
 * (mouse) e foco destacam a UF no mapa (§9.11.4); clique abre `#/governadores/{uf}`. Mesmas classes para os dois lados
 * (só `party-{n}` muda, §16.6).
 */
export function RaceRow({ race: r, current, replay, photos }: RaceRowProps) {
  const source = raceRowLinkSource(r.uf);
  // a linha que sai da página (ou do DOM) com o destaque aceso apaga o que acendeu
  useEffect(() => () => clearMapLink(source), [source]);
  const party = raceParty(r);
  const leader = r.side === null ? null : r.candidates[r.side];
  const status = rowStatus(r);
  const [s0, s1] = r.shares;
  const pct = (v: number) => fmtPctAdaptive(v, s0, s1);
  const highlight = () => setMapLink({ kind: 'place', code: r.uf }, source);
  const clear = () => clearMapLink(source);
  const onEnter = (e: JSX.TargetedPointerEvent<HTMLButtonElement>) => {
    if (e.pointerType === 'mouse') highlight();
  };

  return (
    <button
      type="button"
      class={`race-row race-row--${r.phase}`}
      data-testid="race-row"
      data-uf={r.uf}
      data-phase={r.phase}
      aria-label={rowAria(r)}
      aria-current={current ? 'true' : undefined}
      onClick={() => navigate({ office: 'governor', place: { kind: 'uf', uf: r.uf } })}
      onPointerEnter={onEnter}
      onPointerLeave={clear}
      onFocus={highlight}
      onBlur={clear}
    >
      <span class="race-row__seal">
        <UfTile code={r.uf} fill={tileFill(r)} party={party} size="big" />
        {status !== null ? (
          <span class="race-row__status">{status}</span>
        ) : (
          <MiniProgressBar sectionsFraction={r.sectionsFraction} />
        )}
      </span>
      {r.phase === 'no_data' ? (
        <span class="race-row__waiting">
          <span class="race-row__faces">
            {r.candidates.map((c) => (
              <Portrait
                key={c.number}
                candidate={c}
                photo={photos ? c.photo : null}
                size={WAITING_PORTRAIT_PX}
                shape="round"
              />
            ))}
          </span>
          <span class="race-row__waiting-text">{t('gov.row.waiting')}</span>
        </span>
      ) : (
        <span class="race-row__cands">
          {r.candidates.map((c, i) => {
            const lost = r.phase === 'decided' && r.side !== null && r.side !== i;
            return (
              <span
                key={c.number}
                class={`race-row__cand ${partyClass(partyOf(c))}`}
                data-number={c.number}
                data-state={lost ? 'muted' : undefined}
              >
                <Portrait candidate={c} photo={photos ? c.photo : null} size={PORTRAIT_PX} shape="round" muted={lost} />
                <span class="race-row__name">{c.display_name}</span>
                <span class="race-row__pct" data-dataviz="">
                  <AnimatedNumber value={r.shares[i as 0 | 1]} format={pct} />
                </span>
              </span>
            );
          })}
        </span>
      )}
      <WashHighlight leaderKey={leader?.number ?? null} party={party ?? null} replay={replay} />
    </button>
  );
}
